"use client";

import useEmblaCarousel from "embla-carousel-react";
import { testimonialsData } from "@/app/api/page-data/route";
import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function Testimonials() {
    const [emblaRef, emblaApi] = useEmblaCarousel({ 
        loop: true, 
        align: "start",
        containScroll: "trimSnaps"
    });
    
    const [mounted, setMounted] = useState(false);
    const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
    const [nextBtnDisabled, setNextBtnDisabled] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    const onSelect = useCallback((api: any) => {
        setPrevBtnDisabled(!api.canScrollPrev());
        setNextBtnDisabled(!api.canScrollNext());
    }, []);

    useEffect(() => {
        if (!emblaApi) return;
        onSelect(emblaApi);
        emblaApi.on("reInit", onSelect);
        emblaApi.on("select", onSelect);
    }, [emblaApi, onSelect]);

    const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
    const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

    return (
        <section className="container" id="testimonials">
            <div className="border-x border-t border-primary/10 max-w-6xl mx-auto">
                {/* Ujednolicony padding: px-6 sm:px-12 i py-16 */}
                <div className="flex flex-col px-6 sm:px-12 py-16">
                    
                    <div className="lg:pb-12 pb-8">
                        {/* Nagłówek: font-medium, 32px, tracking-tight */}
                        <h2 className="text-2xl sm:text-3xl lg:text-[32px] font-medium tracking-tight lg:md:text-center text-start">
                            Opinie o współpracy
                        </h2>
                    </div>

                    <div className="overflow-hidden cursor-grab active:cursor-grabbing" ref={emblaRef}>
                        <div className="flex gap-6">
                            {testimonialsData.map((item, index) => (
                                <div key={index} className="flex-[0_0_100%] md:flex-[0_0_calc(50%-12px)] min-w-0">
                                    <div className="flex flex-col h-full p-8 rounded-3xl border border-primary/5 bg-primary/[0.02]">
                                        <blockquote className="text-lg text-primary/80 leading-relaxed mb-8 italic">
                                            &bdquo;{item.quote}&rdquo;
                                        </blockquote>
                                        <div className="mt-auto flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
                                                {item.author.charAt(0)}
                                            </div>
                                            <div>
                                                <p className="font-semibold text-primary text-sm">{item.author}</p>
                                                <p className="text-xs text-secondary mt-0.5">{item.role}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex gap-4 justify-start items-center pt-10">
                        <button 
                            onClick={scrollPrev}
                            disabled={!mounted ? true : prevBtnDisabled}
                            className="p-3 rounded-full border border-primary/10 transition-all
                                    disabled:opacity-20 disabled:cursor-not-allowed
                                    enabled:hover:bg-primary enabled:hover:text-white enabled:active:scale-95 shadow-sm"
                            aria-label="Poprzednia opinia"
                        >
                            <ChevronLeft size={20} />
                        </button>

                        <button 
                            onClick={scrollNext}
                            disabled={!mounted ? false : nextBtnDisabled}
                            className="p-3 rounded-full border border-primary/10 transition-all
                                    disabled:opacity-20 disabled:cursor-not-allowed
                                    enabled:hover:bg-primary enabled:hover:text-white enabled:active:scale-95 shadow-sm"
                            aria-label="Następna opinia"
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}